import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { ShoppingCart, User, LogOut, LayoutDashboard, Package, Menu, X, Search } from "lucide-react";
import AdminChatDock from "@/components/AdminChatDock";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count, bump } = useCart();
  const navigate = useNavigate();
  const [animate, setAnimate] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");
  const [q, setQ] = useState("");
  const onSearch = (e) => { e.preventDefault(); const s = q.trim(); navigate(s ? `/produk?q=${encodeURIComponent(s)}` : "/produk"); setMobileOpen(false); };

  useEffect(() => {
    api.get("/settings/logo").then((r) => setLogoUrl(r.data?.logo_url || "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (bump > 0) {
      setAnimate(true);
      const t = setTimeout(() => setAnimate(false), 400);
      return () => clearTimeout(t);
    }
  }, [bump]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const links = [
    { to: "/", label: "Beranda" },
    { to: "/produk", label: "Produk" },
    { to: "/blog", label: "Blog" },
    { to: "/tentang", label: "Tentang Kami" },
    { to: "/hubungi", label: "Hubungi Kami" },
  ];

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-100 no-print">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" data-testid="nav-logo"><BrandLogo logoUrl={logoUrl} /></Link>
          <div className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium text-slate-600 hover:text-[#F0005A] transition-colors">
                {l.label}
              </Link>
            ))}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <form onSubmit={onSearch} className="hidden lg:flex items-center relative">
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="nav-search-input" placeholder="Cari produk..."
                className="w-44 h-9 pl-9 pr-3 rounded-full bg-slate-100 text-sm outline-none focus:ring-2 focus:ring-[#F0005A]/30 focus:bg-white transition" />
              <Search className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
            </form>
            <AdminChatDock />
            <Link to="/keranjang" data-testid="nav-cart-btn" className="relative p-2 rounded-full hover:bg-slate-100 transition-colors">
              <ShoppingCart className={`w-5 h-5 text-[#00133F] ${animate ? "animate-cart-bounce" : ""}`} />
              {count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-[#F0005A] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="nav-user-menu" className="flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-slate-100 transition-colors">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#00133F] flex items-center justify-center text-white text-sm font-semibold">
                        {user.name?.[0]?.toUpperCase()}
                      </div>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-semibold text-[#00133F] truncate">{user.name}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-orders" onClick={() => navigate("/pesanan")}>
                    <Package className="w-4 h-4 mr-2" /> Pesanan Saya
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Panel Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="menu-logout" onClick={handleLogout} className="text-red-600 focus:text-red-600">
                    <LogOut className="w-4 h-4 mr-2" /> Keluar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button data-testid="nav-login-btn" onClick={() => navigate("/login")}
                className="rounded-full bg-[#00133F] hover:bg-[#00133F]/90 text-white h-9 px-5 text-sm">
                <User className="w-4 h-4 mr-1.5" /> Masuk
              </Button>
            )}
            <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t border-slate-100 bg-white px-4 py-3 space-y-1">
          <form onSubmit={onSearch} className="flex items-center relative mb-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="nav-search-input-mobile" placeholder="Cari produk..."
              className="w-full h-10 pl-9 pr-3 rounded-full bg-slate-100 text-sm outline-none focus:ring-2 focus:ring-[#F0005A]/30" />
            <Search className="w-4 h-4 text-slate-400 absolute left-3 pointer-events-none" />
          </form>
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
              className="block py-2 text-sm font-medium text-slate-600">{l.label}</Link>
          ))}
        </div>
      )}
    </nav>
  );
}
